import * as THREE from 'three';

export const createPowerUpScene = (scene, surfaceY) => {
  const aura = new THREE.Group();
  scene.add(aura);
  const glowMaterial = new THREE.MeshBasicMaterial({
    color: 0xffdb57,
    transparent: true,
    opacity: 0.18,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  });
  const shell = new THREE.Mesh(
    new THREE.SphereGeometry(0.85, 20, 16),
    glowMaterial
  );
  shell.scale.set(1, 1.5, 1);
  aura.add(shell);
  const flameMaterial = glowMaterial.clone();
  flameMaterial.color.set(0xffc728);
  flameMaterial.opacity = 0.48;
  const flames = Array.from({ length: 10 }, (_, index) => {
    const angle = (index * Math.PI) / 5;
    const flame = new THREE.Mesh(
      new THREE.ConeGeometry(0.13, 1.1, 5),
      flameMaterial
    );
    flame.position.set(Math.cos(angle) * 0.62, 0.1, Math.sin(angle) * 0.62);
    flame.rotation.set(Math.sin(angle) * 0.22, angle, -Math.cos(angle) * 0.22);
    aura.add(flame);
    return flame;
  });
  const bolts = Array.from({ length: 8 }, () => {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array(18), 3)
    );
    const bolt = new THREE.Line(
      geometry,
      new THREE.LineBasicMaterial({
        color: 0xfff4aa,
        transparent: true,
        opacity: 0.85,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      })
    );
    aura.add(bolt);
    return bolt;
  });

  const blast = new THREE.Group();
  scene.add(blast);
  const beamMaterial = glowMaterial.clone();
  beamMaterial.color.set(0x8efaff);
  beamMaterial.opacity = 0.9;
  const beam = new THREE.Mesh(
    new THREE.CylinderGeometry(0.14, 0.65, 1, 16),
    beamMaterial
  );
  blast.add(beam);
  const beamHalo = new THREE.Mesh(
    new THREE.CylinderGeometry(0.38, 0.95, 1, 16),
    glowMaterial.clone()
  );
  blast.add(beamHalo);
  const wave = new THREE.Mesh(
    new THREE.RingGeometry(0.7, 0.9, 48),
    beamMaterial.clone()
  );
  wave.rotation.x = -Math.PI / 2;
  blast.add(wave);
  const shards = new THREE.InstancedMesh(
    new THREE.BoxGeometry(0.22, 0.14, 0.3),
    new THREE.MeshStandardMaterial({
      color: 0x716034,
      emissive: 0xffa62f,
      emissiveIntensity: 1.2,
      transparent: true,
    }),
    20
  );
  shards.frustumCulled = false;
  blast.add(shards);
  const fragment = new THREE.Object3D();

  return {
    update({
      stage,
      elapsed = 0,
      progress = 0,
      diePosition,
      tilePosition,
      reduceMotion,
    }) {
      aura.visible = !!stage;
      blast.visible = stage === 'blast' || (stage === 'flight' && elapsed < 1);
      if (!stage) return;
      aura.position.copy(diePosition);
      const charging = stage === 'charging';
      const pulse = reduceMotion ? 0 : Math.sin(elapsed * 18) * 0.07;
      aura.scale.setScalar(charging ? 0.9 + progress * 0.6 + pulse : 1);
      glowMaterial.opacity = charging ? 0.12 + progress * 0.2 : 0.12;
      flames.forEach((flame, index) => {
        flame.visible = charging && !reduceMotion;
        flame.scale.y =
          0.6 +
          progress +
          (reduceMotion ? 0 : Math.sin(elapsed * 12 + index) * 0.15);
      });
      bolts.forEach((bolt, index) => {
        bolt.visible = charging && !reduceMotion;
        if (!bolt.visible) return;
        const positions = bolt.geometry.attributes.position.array;
        const angle = (index * Math.PI) / 4 + elapsed * 0.7;
        for (let point = 0; point < 6; point += 1) {
          const jitter = Math.sin(elapsed * 24 + point * 8 + index) * 0.15;
          const radius = 0.65 + jitter;
          positions[point * 3] = Math.cos(angle) * radius;
          positions[point * 3 + 1] = -0.55 + point * 0.33;
          positions[point * 3 + 2] = Math.sin(angle) * radius;
        }
        bolt.geometry.attributes.position.needsUpdate = true;
      });
      if (!blast.visible) return;
      const time = Math.min(1, elapsed);
      beam.visible = beamHalo.visible = stage === 'blast';
      const height = Math.max(0.3, diePosition.y - surfaceY);
      beam.position.set(tilePosition.x, surfaceY + height / 2, tilePosition.z);
      beam.scale.y = height;
      beamHalo.position.copy(beam.position);
      beamHalo.scale.y = height;
      wave.position.set(tilePosition.x, surfaceY + 0.08, tilePosition.z);
      wave.scale.setScalar(reduceMotion ? 1.5 : 0.3 + time * 6);
      wave.material.opacity = Math.max(0, 0.9 - time * 1.3);
      shards.visible = !reduceMotion;
      shards.material.opacity = Math.max(0, 1 - time);
      for (let index = 0; index < shards.count; index += 1) {
        const angle = index * 2.39996;
        const speed = 2 + (index % 5) * 0.8;
        fragment.position.set(
          tilePosition.x + Math.cos(angle) * speed * time,
          surfaceY + 0.12 + (3 + (index % 4)) * time - 5 * time * time,
          tilePosition.z + Math.sin(angle) * speed * time
        );
        fragment.rotation.set(index + time * 6, angle + time * 4, time * 5);
        fragment.scale.setScalar(Math.max(0.01, 1 - time));
        fragment.updateMatrix();
        shards.setMatrixAt(index, fragment.matrix);
      }
      shards.instanceMatrix.needsUpdate = true;
    },
  };
};
